import React, { useEffect, useState, Fragment } from "react";
import { useDispatch } from "react-redux";
import { Row, Col, Card, Button, Table } from "react-bootstrap";
import { Fn_FillListData, Fn_GetReport } from "../../store/Functions";
import { API_WEB_URLS } from "../../constants/constAPI";
import PageTitle from "../layouts/PageTitle";
import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";
import ExcelJS from "exceljs";

const MONTH_NAMES = [
  "January", "February", "March", "April", "May", "June",
  "July", "August", "September", "October", "November", "December"
];


const currentYear = new Date().getFullYear();
const YEAR_LIST = Array.from({ length: 11 }, (_, i) => currentYear - 5 + i);

const WagesRegisterDeptWise = () => {
  const dispatch = useDispatch();

  // Read payroll sessions
  const readSession = () => ({
    firm: (() => {
      try {
        return JSON.parse(localStorage.getItem("selectedFirm") || "null");
      } catch {
        return null;
      }
    })(),
    month: localStorage.getItem("selectedPayrollMonth") || "",
    year: parseInt(localStorage.getItem("selectedPayrollYear") || "0") || currentYear,
  });

  const [sessionFirm, setSessionFirm] = useState(() => readSession().firm);
  const [selectedMonth, setSelectedMonth] = useState(() => readSession().month);
  const [selectedYear, setSelectedYear] = useState(() => readSession().year);

  // States
  const [firmList, setFirmList] = useState([]);
  const [reportData, setReportData] = useState(null);
  const [deptList, setDeptList] = useState([]);
  const [designations, setDesignations] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const [selectedDept, setSelectedDept] = useState("");

  // Sync session changes from storage
  useEffect(() => {
    const onStorage = () => {
      const s = readSession();
      setSessionFirm(s.firm);
      setSelectedMonth(s.month);
      setSelectedYear(s.year);
    };
    window.addEventListener("storage", onStorage);
    return () => window.removeEventListener("storage", onStorage);
  }, []);

  // Fetch initial masters
  useEffect(() => {
    const firmApiUrl = `${API_WEB_URLS.MASTER}/0/token/${API_WEB_URLS.FirmMaster}/Id/0`;
    Fn_FillListData(dispatch, setFirmList, "gridData", firmApiUrl);

    Fn_FillListData(dispatch, setDeptList, "gridData", `${API_WEB_URLS.MASTER}/0/token/DepartmentMaster/Id/0`);
    Fn_FillListData(dispatch, setDesignations, "gridData", `${API_WEB_URLS.MASTER}/0/token/DesignationMaster/Id/0`);
  }, [dispatch]);

  // Load report data when inputs change
  useEffect(() => {
    const firmId = sessionFirm?.Id || 0;
    if (firmId && selectedMonth && selectedYear) {
      loadReportData(firmId, selectedMonth, selectedYear, selectedDept);
    } else {
      setReportData(null);
    }
  }, [sessionFirm, selectedMonth, selectedYear, selectedDept]);

  const loadReportData = async (firmId, monthName, year, departmentId) => {
    setIsLoading(true);
    try {
      if (monthName && year) {
        const monthAbbr = monthName ? monthName.substring(0, 3) : "";

        const fd = new FormData();
        fd.append("F_FirmMaster", firmId);
        fd.append("PayrollMonth", monthAbbr);
        fd.append("PayrollYear", year);
        fd.append("F_DepartmentMaster", departmentId || 0);

        const records = await Fn_GetReport(
          dispatch,
          () => {},
          "data",
          "GetWagesRegister_DepartmentWise/0/token",
          { arguList: { id: 0, formData: fd } },
          true
        );

        let parsedData = null;
        if (records && records.length > 0) {
          const firstKey = Object.keys(records[0])[0];
          if (firstKey) {
            const jsonString = records.map(row => row[firstKey] || "").join("");
            if (jsonString) {
              parsedData = JSON.parse(jsonString);
            }
          }
        }
        setReportData(parsedData);
      } else {
        setReportData(null);
      }
    } catch (err) {
      console.error("Error loading report data:", err);
      setReportData(null);
    } finally {
      setIsLoading(false);
    }
  };

  const getFirmPfNo = () => {
    if (reportData?.FirmDetails?.PFNo) return reportData.FirmDetails.PFNo;
    if (!sessionFirm || !sessionFirm.BranchData) return "N/A";
    try {
      const branches = JSON.parse(sessionFirm.BranchData);
      if (Array.isArray(branches) && branches.length > 0) {
        const mainBranch = branches.find(b => b.IsMainBranch === 1) || branches[0];
        return mainBranch.PFNo || "N/A";
      }
    } catch (e) {}
    return "N/A";
  };

  const getFirmEsiNo = () => {
    if (reportData?.FirmDetails?.ESINo) return reportData.FirmDetails.ESINo;
    if (!sessionFirm || !sessionFirm.BranchData) return "N/A";
    try {
      const branches = JSON.parse(sessionFirm.BranchData);
      if (Array.isArray(branches) && branches.length > 0) {
        const mainBranch = branches.find(b => b.IsMainBranch === 1) || branches[0];
        return mainBranch.ESINo || "N/A";
      }
    } catch (e) {}
    return "N/A";
  };

  // Grouping logic (using API data)
  const getGroupedDepartments = () => {
    if (!reportData || !reportData.Departments) return [];

    return reportData.Departments.map(dept => {
      const deptName = dept.DepartmentName?.toUpperCase() || "UNASSIGNED STAFF";
      const records = dept.Records || dept.Employees || [];
      const query = searchTerm.toLowerCase();

      const mappedRecords = [];
      let basicStd = 0, grossStd = 0, basicEarned = 0, grossEarned = 0, pf = 0, esi = 0, net = 0;

      records.forEach(rec => {
        // Filter search term
        const matchSearch = (rec.EmployeeName || rec.Name)?.toLowerCase().includes(query) || 
                            (rec.FatherName)?.toLowerCase().includes(query) || 
                            (rec.SerialNo || rec.AcNo || String(rec.Sno))?.toLowerCase().includes(query);
        if (!matchSearch) return;

        const row = {
          acNo: rec.SerialNo || rec.AcNo || "",
          name: (rec.EmployeeName || rec.Name || "").toUpperCase(),
          fatherName: (rec.FatherName || "").toUpperCase(),
          gender: rec.Sex || rec.Gender || "M",
          designation: rec.Designation || "STAFF",
          pfNo: rec.PFNo || "N/A",
          uan: rec.UAN || "N/A",
          esiNo: rec.ESINo || "N/A",
          cardNo: rec.CardNo || "N/A",
          attendance: {
            wd: parseFloat(rec.Attendance?.WD || rec.WorkedDay || rec.WorkedDays || rec.WD || 0),
            abs: parseFloat(rec.Attendance?.LW || rec.Attendance?.Absent || rec.Absent || rec.AbsentDays || rec.LW || 0),
            fh: parseFloat(rec.Attendance?.FH || rec.Attendance?.FestiveHoliday || rec.FestiveHoliday || rec.FH || 0),
            leaves: parseFloat(rec.Attendance?.Leaves || (parseFloat(rec.Attendance?.CL || rec.CL || 0) + parseFloat(rec.Attendance?.SL || rec.SL || 0) + parseFloat(rec.Attendance?.ML || rec.ML || 0)) || 0),
            pd: parseFloat(rec.Attendance?.PD || rec.Attendance?.PaidDay || rec.Attendance?.PaidDays || rec.PaidDay || rec.PD || 0)
          },
          standard: {
            basic: parseFloat(rec.StandardRates?.Basic || rec.BasicRate || rec.BasicSalary || 0),
            hra: parseFloat(rec.StandardRates?.HRA || rec.HRARate || rec.HRA || 0),
            con: parseFloat(rec.StandardRates?.Conveyance || rec.ConveyanceRate || rec.Conveyance || 0),
            total: parseFloat(rec.StandardRates?.Total || rec.GrossRate || rec.GrossSalary || rec.Total || 0)
          },
          earned: {
            basic: parseFloat(rec.EarnedWages?.Basic || rec.EarnedBasic || rec.Wages || 0),
            hra: parseFloat(rec.EarnedWages?.HRA || rec.EarnedHRA || rec.CalculatedAllowance1 || 0),
            con: parseFloat(rec.EarnedWages?.Conveyance || rec.EarnedConveyance || rec.CalculatedAllowance2 || 0),
            ot: parseFloat(rec.EarnedWages?.OTAmount || rec.OverTime || 0) + parseFloat(rec.EarnedWages?.Arrears || rec.Arrear || 0),
            gross: parseFloat(rec.EarnedWages?.GrossAmount || rec.GrossEarned || rec.GrossAmount || 0)
          },
          deductions: {
            pf: Math.round(parseFloat(rec.Deductions?.PF || rec.PFDeduction || rec.PF || 0)),
            pen: Math.round(parseFloat(rec.Deductions?.Pension || rec.Pension || 0)),
            esi: parseFloat(rec.Deductions?.ESI || rec.ESIDeduction || rec.ESI || 0),
            tds: parseFloat(rec.Deductions?.TDS || rec.TDS || 0),
            loan: parseFloat(rec.Deductions?.LoanAdv || rec.LoanAdv || 0) + parseFloat(rec.Deductions?.Recovery || rec.Recovery || 0),
            total: parseFloat(rec.Deductions?.TotalDeductions || rec.TotalDeduction || 0)
          },
          net: parseFloat(rec.NetPayable || rec.NetPayment || 0)
        };

        mappedRecords.push(row);
        basicStd += row.standard.basic;
        grossStd += row.standard.total;
        basicEarned += row.earned.basic;
        grossEarned += row.earned.gross;
        pf += row.deductions.pf;
        esi += row.deductions.esi;
        net += row.net;
      });

      return {
        name: deptName,
        records: mappedRecords,
        totals: {
          basicStd,
          grossStd,
          basicEarned,
          grossEarned,
          pf,
          esi,
          net
        }
      };
    }).filter(d => d.records.length > 0);
  };

  const departments = getGroupedDepartments();

  // Excel Export Preserving double row structure
  const exportToExcel = async () => {
    try {
      const workbook = new ExcelJS.Workbook();
      const worksheet = workbook.addWorksheet("Wages Register");

      worksheet.addRow([sessionFirm?.Name?.toUpperCase() || ""]);
      worksheet.addRow([`WAGES REGISTER DEPARTMENT-WISE FOR THE MONTH OF ${selectedMonth?.toUpperCase()} ${selectedYear}`]);
      worksheet.addRow([`PF CODE: ${getFirmPfNo()}`, "", "", `ESI CODE: ${getFirmEsiNo()}`]);
      worksheet.addRow([]);

      const header = worksheet.addRow([
        "Sn", "Employee Name / Father", "Designation", "PF No / ESI No / UAN", 
        "WD", "LW", "FH", "CL/ML", "PD",
        "STD BASIC", "STD GROSS", 
        "EARNED BASIC", "EARNED GROSS", 
        "PF DED.", "ESI DED.", "TOTAL DED.", 
        "NET PAYABLE"
      ]);
      header.font = { bold: true };

      departments.forEach(dept => {
        worksheet.addRow([`DEPT: ${dept.name}`]).font = { bold: true };
        
        dept.records.forEach((rec, idx) => {
          worksheet.addRow([
            idx + 1,
            rec.name,
            rec.designation,
            `PF: ${rec.pfNo}`,
            rec.attendance.wd,
            rec.attendance.abs,
            rec.attendance.fh,
            rec.attendance.leaves,
            rec.attendance.pd,
            rec.standard.basic,
            rec.standard.total,
            rec.earned.basic,
            rec.earned.gross,
            rec.deductions.pf,
            rec.deductions.esi,
            rec.deductions.total,
            rec.net
          ]);
          worksheet.addRow([
            "",
            rec.fatherName,
            "",
            `UAN: ${rec.uan} | ESI: ${rec.esiNo}`,
            "", "", "", "", "",
            "", "", "", "",
            `Pen: ${rec.deductions.pen}`,
            "", "", ""
          ]);
        });

        // Add Dept Total
        worksheet.addRow([
          `TOTAL FOR ${dept.name}`, "", "", "", "", "", "", "", "",
          dept.totals.basicStd,
          dept.totals.grossStd,
          dept.totals.basicEarned,
          dept.totals.grossEarned,
          dept.totals.pf,
          dept.totals.esi,
          "",
          dept.totals.net
        ]).font = { bold: true };
      });

      const buffer = await workbook.xlsx.writeBuffer();
      const blob = new Blob([buffer], { type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet" });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.download = `Wages_Register_${selectedMonth}_${selectedYear}.xlsx`;
      link.click();
    } catch (e) {
      alert("Excel export failed.");
    }
  };

  // PDF Export
  const exportToPDF = () => {
    const pdf = new jsPDF("l", "mm", "a4"); // Landscape layout

    pdf.setFontSize(11);
    pdf.setFont("Helvetica", "bold");
    pdf.text(sessionFirm?.Name?.toUpperCase() || "", 148, 12, { align: "center" });
    pdf.text(`WAGES REGISTER DEPARTMENT-WISE FOR THE MONTH OF ${selectedMonth?.toUpperCase()} ${selectedYear}`, 148, 17, { align: "center" });

    pdf.setFontSize(8.5);
    pdf.text(`PF CODE: ${getFirmPfNo()}`, 14, 23);
    pdf.text(`ESI CODE: ${getFirmEsiNo()}`, 220, 23);

    const body = [];
    departments.forEach(dept => {
      // Dept Header Row
      body.push([
        { content: `DEPARTMENT: ${dept.name}`, colSpan: 17, styles: { fontStyle: "bold", fillColor: [240, 240, 240] } }
      ]);

      dept.records.forEach((rec, idx) => {
        body.push([
          idx + 1,
          rec.name,
          rec.designation,
          rec.pfNo,
          rec.attendance.wd.toString(),
          rec.attendance.abs.toString(),
          rec.attendance.fh.toString(),
          rec.attendance.leaves.toString(),
          rec.attendance.pd.toString(),
          rec.standard.basic.toFixed(2),
          rec.standard.total.toFixed(2),
          rec.earned.basic.toFixed(2),
          rec.earned.gross.toFixed(2),
          rec.deductions.pf.toString(),
          rec.deductions.esi.toFixed(2),
          rec.deductions.total.toFixed(2),
          rec.net.toFixed(2)
        ]);
        body.push([
          "",
          { content: rec.fatherName, styles: { textColor: [120, 120, 120] } },
          "",
          { content: `UAN: ${rec.uan}`, styles: { textColor: [120, 120, 120] } },
          "", "", "", "", "", "", "", "", "",
          { content: `Pen: ${rec.deductions.pen}`, styles: { textColor: [120, 120, 120] } },
          "", "", ""
        ]);
      });

      // Dept Totals Row
      body.push([
        { content: `TOTAL ${dept.name}`, colSpan: 9, styles: { fontStyle: "bold", halign: "right" } },
        { content: dept.totals.basicStd.toFixed(2), styles: { fontStyle: "bold" } },
        { content: dept.totals.grossStd.toFixed(2), styles: { fontStyle: "bold" } },
        { content: dept.totals.basicEarned.toFixed(2), styles: { fontStyle: "bold" } },
        { content: dept.totals.grossEarned.toFixed(2), styles: { fontStyle: "bold" } },
        { content: dept.totals.pf.toString(), styles: { fontStyle: "bold" } },
        { content: dept.totals.esi.toFixed(2), styles: { fontStyle: "bold" } },
        "",
        { content: dept.totals.net.toFixed(2), styles: { fontStyle: "bold" } }
      ]);
    });

    autoTable(pdf, {
      head: [[
        "Sn", "Employee Name / Father", "Designation", "PF No / UAN", 
        "WD", "LW", "FH", "CL", "PD",
        "STD BASIC", "STD GROSS", 
        "EARN BASIC", "EARN GROSS", 
        "PF DED.", "ESI DED.", "TOT DED.", 
        "NET PAYABLE"
      ]],
      body,
      startY: 27,
      styles: { fontSize: 6.5, cellPadding: 1, lineColor: [0,0,0], lineWidth: 0.05 },
      headStyles: { fillColor: [44, 62, 80], textColor: [255, 255, 255] }
    });

    pdf.save(`Wages_Register_${selectedMonth}_${selectedYear}.pdf`);
  };

  const printStyles = `
    @media print {
      body * {
        visibility: hidden !important;
      }
      #print-wages-container, #print-wages-container * {
        visibility: visible !important;
      }
      #print-wages-container {
        display: block !important;
        position: absolute;
        left: 0;
        top: 0;
        width: 100%;
        background: #fff;
        color: #000;
        font-family: Arial, sans-serif;
      }
      .no-print {
        display: none !important;
      }
      .wages-print-table {
        border-collapse: collapse;
        width: 100%;
        margin-top: 15px;
      }
      .wages-print-table th, .wages-print-table td {
        border: 1px solid #000;
        padding: 4px;
        font-size: 9px;
      }
      .wages-print-table th {
        font-weight: bold;
        text-align: center;
        background: #f2f2f2;
      }
    }
  `;

  return (
    <Fragment>
      <style>{printStyles}</style>

      <div className="no-print">
        <PageTitle activeMenu="Wages Register" motherMenu="Reports" pageContent="Wages Register Department-wise" />
      </div>

      {/* Filter Card */}
      <Card className="no-print mb-4 shadow-sm border-0" style={{ borderRadius: "10px", background: "#ffffff" }}>
        <Card.Body className="p-4">
          <Row className="align-items-end g-3">
            {/* Firm */}
            <Col lg={3} md={6}>
              <label className="form-label fw-semibold fs-13" style={{ color: "#495057" }}>
                Firm <span className="badge bg-light text-success ms-1 border" style={{ fontSize: "11px" }}>🔒 Header Locked</span>
              </label>
              <input
                type="text"
                className="form-control"
                style={{ height: "38px", borderRadius: "6px", fontSize: "13px", backgroundColor: "#f8f9fa", cursor: "not-allowed" }}
                value={sessionFirm?.Name || "Please select Firm from Header"}
                readOnly
                disabled
              />
            </Col>

            {/* Department */}
            <Col lg={3} md={6}>
              <label className="form-label fw-semibold fs-13" style={{ color: "#495057" }}>Department</label>
              <select
                className="form-select"
                style={{ height: "38px", borderRadius: "6px", fontSize: "13px" }}
                value={selectedDept}
                onChange={(e) => setSelectedDept(e.target.value)}
              >
                <option value="">-- All Departments --</option>
                {deptList.map((d) => (
                  <option key={d.Id} value={d.Id}>{d.Name}</option>
                ))}
              </select>
            </Col>

            {/* Month */}
            <Col lg={1} md={3}>
              <label className="form-label fw-semibold fs-13" style={{ color: "#495057" }}>Month</label>
              <select
                className="form-select"
                style={{ height: "38px", borderRadius: "6px", fontSize: "13px" }}
                value={selectedMonth}
                onChange={(e) => {
                  setSelectedMonth(e.target.value);
                  if (e.target.value) localStorage.setItem("selectedPayrollMonth", e.target.value);
                  else localStorage.removeItem("selectedPayrollMonth");
                }}
              >
                <option value="">-- Month --</option>
                {MONTH_NAMES.map((m) => (
                  <option key={m} value={m}>{m}</option>
                ))}
              </select>
            </Col>

            {/* Year */}
            <Col lg={1} md={3}>
              <label className="form-label fw-semibold fs-13" style={{ color: "#495057" }}>Year</label>
              <select
                className="form-select"
                style={{ height: "38px", borderRadius: "6px", fontSize: "13px" }}
                value={selectedYear}
                onChange={(e) => {
                  setSelectedYear(parseInt(e.target.value) || 0);
                  if (e.target.value) localStorage.setItem("selectedPayrollYear", e.target.value);
                  else localStorage.removeItem("selectedPayrollYear");
                }}
              >
                {YEAR_LIST.map((y) => (
                  <option key={y} value={y}>{y}</option>
                ))}
              </select>
            </Col>

            {/* Search */}
            <Col lg={4} md={12}>
              <label className="form-label fw-semibold fs-13" style={{ color: "#495057" }}>Search Employee</label>
              <input
                type="text"
                className="form-control"
                style={{ height: "38px", borderRadius: "6px", fontSize: "13px" }}
                placeholder="Search by name, father name, SerialNo..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
              />
            </Col>
          </Row>
        </Card.Body>
      </Card>

      {!sessionFirm?.Id ? (
        <Card className="shadow-sm border-0">
          <Card.Body className="text-center p-5">
            <h5 className="text-warning mb-2 fw-semibold">⚠ Session Setup Required</h5>
            <p className="text-muted mb-0 fs-13">
              Please select a **Firm**, **Month**, and **Year** from the filters above or from the **Dashboard Home** to load the report.
            </p>
          </Card.Body>
        </Card>
      ) : (
        <Fragment>
          {/* ─── WEB VIEW ─── */}
          <div className="no-print">
            <Card className="shadow-sm border-0" style={{ borderRadius: "10px", background: "#ffffff", overflow: "hidden" }}>
              <Card.Header className="d-flex justify-content-between align-items-center bg-white border-bottom py-3 px-4">
                <div>
                  <h5 className="mb-0 text-dark fw-bold fs-16" style={{ fontFamily: "'Poppins', sans-serif" }}>
                    Wages Register (Department-wise)
                  </h5>
                  <small className="text-muted fs-12">
                    PF Code: <strong>{getFirmPfNo()}</strong> &nbsp;|&nbsp; ESI Code: <strong>{getFirmEsiNo()}</strong> &nbsp;|&nbsp; Period: <strong>{(selectedMonth || "").toUpperCase()} {selectedYear}</strong>
                  </small>
                </div>
                <div className="d-flex gap-2">
                  <Button variant="danger" size="sm" className="px-3" style={{ borderRadius: "5px", fontSize: "12px", background: "#E74C3C", border: "none" }} onClick={exportToPDF}>
                    <i className="fa fa-file-pdf-o me-2" />
                    PDF
                  </Button>
                  <Button variant="success" size="sm" className="px-3" style={{ borderRadius: "5px", fontSize: "12px", background: "#27AE60", border: "none" }} onClick={exportToExcel}>
                    <i className="fa fa-file-excel-o me-2" />
                    Excel
                  </Button>
                  <Button variant="primary" size="sm" className="px-3 text-white" style={{ borderRadius: "5px", fontSize: "12px", background: "#2980B9", border: "none" }} onClick={() => window.print()}>
                    <i className="fa fa-print me-2" />
                    Print Form
                  </Button>
                </div>
              </Card.Header>

              <Card.Body className="p-0">
                {isLoading ? (
                  <div className="text-center p-5">
                    <div className="spinner-border text-primary" role="status" />
                    <div className="text-muted mt-2 fs-13">Loading Wages Register...</div>
                  </div>
                ) : (
                  <div className="table-responsive">
                    <Table bordered hover striped={false} className="mb-0 align-middle text-nowrap" style={{ fontSize: "12px", fontFamily: "'Poppins', sans-serif" }}>
                      <thead style={{ background: "#F8F9FA" }}>
                        <tr style={{ borderBottom: "2px solid #E9ECEF" }}>
                          <th rowSpan={2} className="text-center fw-bold" style={{ width: "40px", verticalAlign: "middle" }}>Sn</th>
                          <th className="fw-bold" style={{ width: "220px" }}>EMPLOYEE NAME / FATHER NAME</th>
                          <th className="fw-bold" style={{ width: "130px" }}>DESIGNATION</th>
                          <th className="fw-bold" style={{ width: "160px" }}>PF No / UAN / ESI No</th>
                          <th colSpan={5} className="text-center fw-bold">ATTENDANCE</th>
                          <th colSpan={2} className="text-center fw-bold">STANDARD RATES</th>
                          <th colSpan={3} className="text-center fw-bold">EARNED WAGES</th>
                          <th colSpan={3} className="text-center fw-bold">DEDUCTIONS</th>
                          <th rowSpan={2} className="text-center fw-bold" style={{ verticalAlign: "middle", width: "110px" }}>NET PAYABLE</th>
                          <th rowSpan={2} className="text-center fw-bold" style={{ verticalAlign: "middle", width: "100px" }}>SIGNATURE</th>
                        </tr>
                        <tr style={{ background: "#F8F9FA" }}>
                          <th className="text-muted fw-normal">Sex</th>
                          <th className="text-muted fw-normal">Card No</th>
                          <th></th>
                          <th className="text-center text-muted fw-semibold">WD</th>
                          <th className="text-center text-muted fw-semibold">LW</th>
                          <th className="text-center text-muted fw-semibold">FH</th>
                          <th className="text-center text-muted fw-semibold">CL/ML</th>
                          <th className="text-center text-muted fw-semibold">PD</th>
                          <th className="text-end text-muted fw-semibold">BASIC</th>
                          <th className="text-end text-muted fw-semibold">GROSS</th>
                          <th className="text-end text-muted fw-semibold">BASIC</th>
                          <th className="text-end text-muted fw-semibold">OT/ARR</th>
                          <th className="text-end text-muted fw-semibold">GROSS</th>
                          <th className="text-end text-muted fw-semibold">PF</th>
                          <th className="text-end text-muted fw-semibold">ESI</th>
                          <th className="text-end text-muted fw-semibold">TOTAL</th>
                        </tr>
                      </thead>
                      <tbody>
                        {departments.length === 0 ? (
                          <tr>
                            <td colSpan={18} className="text-center py-5 text-muted">
                              No payroll records found for this period.
                            </td>
                          </tr>
                        ) : (
                          departments.map((dept) => (
                            <Fragment key={dept.name}>
                              {/* Department Header */}
                              <tr className="bg-light-subtle">
                                <td colSpan={18} className="fw-bold text-primary fs-13 py-2 px-3">
                                  📂 DEPARTMENT: {dept.name}
                                </td>
                              </tr>

                              {dept.records.map((row, idx) => (
                                <Fragment key={row.acNo + idx}>
                                  {/* Line 1 */}
                                  <tr style={{ borderBottom: "none" }}>
                                    <td rowSpan={2} className="text-center text-secondary bg-light">{idx + 1}</td>
                                    <td className="fw-bold text-dark">{row.name}</td>
                                    <td rowSpan={2} style={{ verticalAlign: "middle" }}>{row.designation}</td>
                                    <td className="text-dark">PF: <span className="font-monospace fs-11">{row.pfNo}</span></td>
                                    <td className="text-center text-dark">{row.attendance.wd.toFixed(1)}</td>
                                    <td className="text-center text-dark">{row.attendance.abs.toFixed(1)}</td>
                                    <td className="text-center text-dark">{row.attendance.fh.toFixed(1)}</td>
                                    <td className="text-center text-dark">{row.attendance.leaves.toFixed(1)}</td>
                                    <td className="text-center text-dark fw-semibold">{row.attendance.pd.toFixed(2)}</td>
                                    <td className="text-end text-dark">₹{row.standard.basic.toLocaleString('en-IN', { minimumFractionDigits: 2 })}</td>
                                    <td className="text-end text-dark">₹{row.standard.total.toLocaleString('en-IN', { minimumFractionDigits: 2 })}</td>
                                    <td className="text-end text-dark">₹{row.earned.basic.toLocaleString('en-IN', { minimumFractionDigits: 2 })}</td>
                                    <td className="text-end text-dark">₹{row.earned.ot.toLocaleString('en-IN', { minimumFractionDigits: 2 })}</td>
                                    <td className="text-end text-dark fw-semibold">₹{row.earned.gross.toLocaleString('en-IN', { minimumFractionDigits: 2 })}</td>
                                    <td className="text-end text-dark">₹{row.deductions.pf.toLocaleString('en-IN')}</td>
                                    <td className="text-end text-dark">₹{row.deductions.esi.toLocaleString('en-IN', { minimumFractionDigits: 2 })}</td>
                                    <td className="text-end text-dark fw-semibold">₹{row.deductions.total.toLocaleString('en-IN', { minimumFractionDigits: 2 })}</td>
                                    <td rowSpan={2} className="text-center text-dark fw-bold fs-13 bg-light-subtle" style={{ verticalAlign: "middle" }}>
                                      ₹{row.net.toLocaleString('en-IN', { minimumFractionDigits: 2 })}
                                    </td>
                                    <td rowSpan={2} className="text-center text-muted" style={{ verticalAlign: "middle" }}>[ Signature ]</td>
                                  </tr>
                                  {/* Line 2 */}
                                  <tr style={{ borderTop: "none", borderBottom: "1px solid #DEE2E6" }}>
                                    <td className="text-muted fs-11">{row.fatherName}</td>
                                    <td className="text-muted font-monospace fs-11">UAN: {row.uan} | ESI: {row.esiNo}</td>
                                    <td className="text-muted fs-11">Gender: {row.gender} | Card: {row.cardNo}</td>
                                    <td colSpan={5} className="text-center text-muted">—</td>
                                    <td colSpan={2} className="text-center text-muted">—</td>
                                    <td colSpan={3} className="text-center text-muted">—</td>
                                    <td className="text-end text-muted font-monospace fs-11">Pen: ₹{row.deductions.pen}</td>
                                    <td colSpan={2} className="text-center text-muted">—</td>
                                  </tr>
                                </Fragment>
                              ))}

                              {/* Department Subtotal */}
                              <tr className="bg-light fw-bold" style={{ borderTop: "2px solid #DEE2E6" }}>
                                <td colSpan={4} className="text-end text-dark fw-bold pe-4">TOTAL FOR {dept.name}</td>
                                <td colSpan={5} className="text-center text-muted">—</td>
                                <td className="text-end text-dark">₹{dept.totals.basicStd.toLocaleString('en-IN', { minimumFractionDigits: 2 })}</td>
                                <td className="text-end text-dark">₹{dept.totals.grossStd.toLocaleString('en-IN', { minimumFractionDigits: 2 })}</td>
                                <td className="text-end text-dark">₹{dept.totals.basicEarned.toLocaleString('en-IN', { minimumFractionDigits: 2 })}</td>
                                <td className="text-center text-muted">—</td>
                                <td className="text-end text-dark">₹{dept.totals.grossEarned.toLocaleString('en-IN', { minimumFractionDigits: 2 })}</td>
                                <td className="text-end text-dark">₹{dept.totals.pf.toLocaleString('en-IN')}</td>
                                <td className="text-end text-dark">₹{dept.totals.esi.toLocaleString('en-IN', { minimumFractionDigits: 2 })}</td>
                                <td className="text-center text-muted">—</td>
                                <td className="text-center text-primary fw-bold">₹{dept.totals.net.toLocaleString('en-IN', { minimumFractionDigits: 2 })}</td>
                                <td className="text-center text-muted">—</td>
                              </tr>
                            </Fragment>
                          ))
                        )}
                      </tbody>
                    </Table>
                  </div>
                )}
              </Card.Body>
            </Card>
          </div>

          {/* ─── PHYSICAL PRINTING VIEW ─── */}
          <div id="print-wages-container" className="d-none">
            <div style={{ textAlign: "center", fontWeight: "bold", fontSize: "14px" }}>
              {sessionFirm?.Name?.toUpperCase()}
            </div>
            <div style={{ textAlign: "center", fontWeight: "bold", fontSize: "11px", margin: "4px 0" }}>
              WAGES REGISTER DEPARTMENT-WISE FOR THE MONTH OF {(selectedMonth || "").toUpperCase()} {selectedYear}
            </div>
            <div style={{ display: "flex", justifyContent: "space-between", fontSize: "10px", margin: "8px 0" }}>
              <div>PF CODE: {getFirmPfNo()}</div>
              <div>ESI CODE: {getFirmEsiNo()}</div>
            </div>

            <table className="wages-print-table">
              <thead>
                <tr>
                  <th rowSpan={2}>Sn</th>
                  <th style={{ textAlign: "left" }}>EMPLOYEE NAME / FATHER NAME</th>
                  <th>DESIGNATION</th>
                  <th>PF No / UAN / ESI No</th>
                  <th>WD</th>
                  <th>LW</th>
                  <th>FH</th>
                  <th>CL/ML</th>
                  <th>PD</th>
                  <th>STD BASIC</th>
                  <th>STD GROSS</th>
                  <th>EARN BASIC</th>
                  <th>OT/ARR</th>
                  <th>EARN GROSS</th>
                  <th>PF DED.</th>
                  <th>ESI DED.</th>
                  <th>TOT DED.</th>
                  <th>NET PAYABLE</th>
                  <th>SIGNATURE</th>
                </tr>
              </thead>
              <tbody>
                {departments.map((dept) => (
                  <Fragment key={dept.name}>
                    <tr>
                      <td colSpan={19} style={{ fontWeight: "bold", background: "#f5f5f5" }}>
                        DEPT: {dept.name}
                      </td>
                    </tr>
                    {dept.records.map((row, idx) => (
                      <Fragment key={row.acNo + idx}>
                        <tr>
                          <td rowSpan={2} style={{ textAlign: "center" }}>{idx + 1}</td>
                          <td><strong>{row.name}</strong></td>
                          <td rowSpan={2} style={{ textAlign: "center" }}>{row.designation}</td>
                          <td>PF: {row.pfNo}</td>
                          <td style={{ textAlign: "center" }}>{row.attendance.wd.toFixed(1)}</td>
                          <td style={{ textAlign: "center" }}>{row.attendance.abs.toFixed(1)}</td>
                          <td style={{ textAlign: "center" }}>{row.attendance.fh.toFixed(1)}</td>
                          <td style={{ textAlign: "center" }}>{row.attendance.leaves.toFixed(1)}</td>
                          <td style={{ textAlign: "center", fontWeight: "bold" }}>{row.attendance.pd.toFixed(2)}</td>
                          <td style={{ textAlign: "right" }}>{row.standard.basic.toFixed(2)}</td>
                          <td style={{ textAlign: "right" }}>{row.standard.total.toFixed(2)}</td>
                          <td style={{ textAlign: "right" }}>{row.earned.basic.toFixed(2)}</td>
                          <td style={{ textAlign: "right" }}>{row.earned.ot.toFixed(2)}</td>
                          <td style={{ textAlign: "right", fontWeight: "bold" }}>{row.earned.gross.toFixed(2)}</td>
                          <td style={{ textAlign: "right" }}>{row.deductions.pf}</td>
                          <td style={{ textAlign: "right" }}>{row.deductions.esi.toFixed(2)}</td>
                          <td style={{ textAlign: "right", fontWeight: "bold" }}>{row.deductions.total.toFixed(2)}</td>
                          <td rowSpan={2} style={{ textAlign: "right", fontWeight: "bold", background: "#fafafa" }}>{row.net.toFixed(2)}</td>
                          <td rowSpan={2} style={{ textAlign: "center", color: "#666" }}>[ Signature ]</td>
                        </tr>
                        <tr>
                          <td style={{ fontSize: "8px", color: "#555" }}>{row.fatherName}</td>
                          <td style={{ fontSize: "8px", color: "#555" }}>UAN: {row.uan} | ESI: {row.esiNo}</td>
                          <td style={{ fontSize: "8px", color: "#555" }}>Sex: {row.gender} | Card: {row.cardNo}</td>
                          <td colSpan={5} style={{ textAlign: "center" }}>—</td>
                          <td colSpan={2} style={{ textAlign: "center" }}>—</td>
                          <td colSpan={3} style={{ textAlign: "center" }}>—</td>
                          <td style={{ textAlign: "right", fontSize: "8px" }}>Pen: {row.deductions.pen}</td>
                          <td colSpan={2} style={{ textAlign: "center" }}>—</td>
                        </tr>
                      </Fragment>
                    ))}
                    {/* Subtotal */}
                    <tr style={{ fontWeight: "bold", background: "#fafafa" }}>
                      <td colSpan={4} style={{ textAlign: "right" }}>TOTAL FOR {dept.name}</td>
                      <td colSpan={5} style={{ textAlign: "center" }}>—</td>
                      <td style={{ textAlign: "right" }}>{dept.totals.basicStd.toFixed(2)}</td>
                      <td style={{ textAlign: "right" }}>{dept.totals.grossStd.toFixed(2)}</td>
                      <td style={{ textAlign: "right" }}>{dept.totals.basicEarned.toFixed(2)}</td>
                      <td style={{ textAlign: "center" }}>—</td>
                      <td style={{ textAlign: "right" }}>{dept.totals.grossEarned.toFixed(2)}</td>
                      <td style={{ textAlign: "right" }}>{dept.totals.pf}</td>
                      <td style={{ textAlign: "right" }}>{dept.totals.esi.toFixed(2)}</td>
                      <td style={{ textAlign: "center" }}>—</td>
                      <td style={{ textAlign: "right" }}>{dept.totals.net.toFixed(2)}</td>
                      <td></td>
                    </tr>
                  </Fragment>
                ))}
              </tbody>
            </table>
          </div>
        </Fragment>
      )}
    </Fragment>
  );
};

export default WagesRegisterDeptWise;
